import React, { useCallback, useMemo, useEffect } from 'react';
import {
  Descriptions,
  Select,
} from 'antd';
import PropTypes from 'prop-types';

import { stripText } from './excelHelpers';
import HoverHelp from '../HoverHelp';
import OnTraccrCheckbox from '../inputs/OnTraccrCheckbox';

const { Option } = Select;

const matchHeader = (headerOptions, key) => {
  let match;
  headerOptions?.forEach((dh) => {
    const rk = stripText(key);
    const strippedTitle = stripText(dh.title);
    if (rk === strippedTitle) match = dh.key;
  });
  return match;
};

export default function SheetHeaderMatcher({
  selectedSheet = [],
  headerMapping,
  setHeaderMapping,
  headerOptions = [],
  shouldSaveMapping = false,
  setShouldSaveMapping,
}) {
  const onChange = useCallback((key) => (newValue) => {
    setHeaderMapping({
      ...headerMapping,
      [key]: newValue || undefined,
    });
  }, [headerMapping, setHeaderMapping]);

  const headers = useMemo(() => {
    if (!selectedSheet || selectedSheet.length === 0) return [];
    // This will be slow for big sheets that have many sparse rows
    const firstRow = selectedSheet.find((row = {}) => Object.keys(row).length > 0);
    return Object.keys(firstRow);
  }, [selectedSheet]);

  useEffect(() => {
    const initialMapping = {};
    headers.forEach((header) => {
      const foundMatch = matchHeader(headerOptions, header);
      if (foundMatch) initialMapping[foundMatch] = header;
    });
    setHeaderMapping(initialMapping);
  }, [headers, headerOptions]);

  const getHeaderHelpContent = (header) => {
    const helpParts = [];
    if (header.caseSensitiveMatch) {
      helpParts.push('Values in your file must match existing options exactly (case-sensitive).');
    }
    if (header.helperText) {
      helpParts.push(header.helperText);
    }
    if (!helpParts.length) return null;
    return (
      <div style={{ whiteSpace: 'pre-line', maxWidth: 320 }}>
        {helpParts.join('\n\n')}
      </div>
    );
  };

  return (
    <>
      <Descriptions
        title="Select Column Headers"
        column={2}
      >
        {
          headerOptions.map((defaultHeader) => (
            <Descriptions.Item
              key={defaultHeader.key}
              label={(
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {defaultHeader.required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
                  {defaultHeader.title}
                  {getHeaderHelpContent(defaultHeader) && (
                    <HoverHelp
                      content={getHeaderHelpContent(defaultHeader)}
                      placement="topRight"
                    />
                  )}
                </span>
              )}
            >
              <div style={{ display: 'inline-block', height: 32 }}>
                <Select
                  value={headerMapping[defaultHeader.key]}
                  style={{ width: 250, margin: '0px 10px' }}
                  onChange={onChange(defaultHeader.key)}
                  placeholder="Select a column"
                  allowClear
                >
                  {headers.map((header) => (
                    <Option value={header} key={header}>{header}</Option>
                  ))}
                </Select>
              </div>
            </Descriptions.Item>
          ))
        }
      </Descriptions>
      { !!setShouldSaveMapping && (
        <OnTraccrCheckbox
          value={shouldSaveMapping}
          onChange={setShouldSaveMapping}
          style={{ marginTop: 10, marginBottom: 10 }}
          label="Save Mapping"
        />
      )}
    </>
  );
}

SheetHeaderMatcher.propTypes = {
  selectedSheet: PropTypes.arrayOf(PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    [PropTypes.string]: PropTypes.any,
  })),
  headerMapping: PropTypes.shape({
    [PropTypes.string]: PropTypes.string,
  }),
  setHeaderMapping: PropTypes.func,
  headerOptions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.string,
    required: PropTypes.bool,
    caseSensitiveMatch: PropTypes.bool,
    helperText: PropTypes.string,
  })),
  shouldSaveMapping: PropTypes.bool,
  setShouldSaveMapping: PropTypes.func,
};

SheetHeaderMatcher.defaultProps = {
  selectedSheet: [],
  headerMapping: {},
  setHeaderMapping: () => {},
  headerOptions: [],
  shouldSaveMapping: false,
  setShouldSaveMapping: null,
};
